// Shared utilities for building PDF document models
// Keep functions simple and pure for easy unit testing

export function escapeHtml(input: string): string {
  if (!input) return ''
  return input
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

// Remove wrapping quotes/parentheses and common year suffixes like ": 2024-2025"
export function normalizeTitleForCover(rawTitle: string): string {
  const base = (rawTitle || '').trim()
  // strip wrapping quotes or parentheses
  const unwrapped = base.replace(/^['"“”‘’(]+|['"“”‘’)\s]+$/g, '')
  // strip trailing year ranges often appended to titles
  return unwrapped.replace(/:\s*\d{4}[-–]\d{4}["']?$/, '').trim()
}

export function sanitizeTitleForCover(rawTitle: string): string {
  return escapeHtml(normalizeTitleForCover(rawTitle))
}

// Create a citation processor that assigns sequential numbers to unique URLs
export function createCitationProcessor() {
  let citationCounter = 0
  const citations: { [num: number]: string } = {}
  const citationMap: { [url: string]: number } = {}

  const assignNumber = (url: string) => {
    const normalized = url
      .replace(/#:~:text=[^\s)\]"]*/g, '')
      .replace(/\)+$/, '')
    if (!citationMap[normalized]) {
      citationCounter += 1
      citationMap[normalized] = citationCounter
      citations[citationCounter] = normalized
    }
    return citationMap[normalized]
  }

  const process = (text: string): string => {
    if (!text) return ''
    // 1) Markdown links: [text](url) -> text[number]
    let processed = text.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_m, linkText, url) => {
      const n = assignNumber(url)
      return `${linkText}[${n}]`
    })
    // 2) Standalone https?:// URLs (optionally wrapped in parentheses)
    processed = processed.replace(/\(?(https?:\/\/[^\s)]+)\)?/g, (_m, url) => {
      const n = assignNumber(url)
      return `[${n}]`
    })
    // 3) Bare domains like www.domain.com or domain.com/path
    processed = processed.replace(
      /\(?\b((?:www\.)?[a-zA-Z0-9][a-zA-Z0-9-]*\.(?:com|org|net|edu|gov|co|io|ai|app|dev|tech|info|biz)(?:[^\s<)]*)?)\)?/g,
      (_m, domain: string) => {
        // normalize to https URL
        const url = domain.startsWith('http') ? domain : `https://${domain}`
        const n = assignNumber(url)
        return `[${n}]`
      }
    )
    return processed
  }

  return { process, citations }
}

export function reduceExcessiveBold(markdown: string): string {
  if (!markdown) return ''

  return markdown.replace(/\*\*(.+?)\*\*/g, (match, content: string) => {
    if (content.length <= 50 && /:\s*$/.test(content)) return match

    if (content.length > 80) return content

    if (/^\$|\$\d|(?:billion|million|trillion|%|YoY|CapEx)\b/i.test(content)) {
      return content
    }

    return match
  })
}

export function addSuperscriptsToCitations(html: string): string {
  if (!html) return ''
  return html.replace(/\[(\d+)\]/g, '<sup>[$1]</sup>')
}

export interface ReportPdfTocEntry {
  id: string
  title: string
  level: 2 | 3
}

export interface ReportPdfReference {
  number: number
  url: string
}

export interface ReportPdfModel {
  title: string
  coverImageUrl?: string | null
  category?: string | null
  generatedLabel?: string
  preparedBy?: string | null
  summary?: string
  keyFindings: string[]
  reportMarkdown: string
  tocEntries: ReportPdfTocEntry[]
  references: ReportPdfReference[]
}

interface ExtractReportHeadingEntriesOptions {
  excludeTitles?: string[]
  maxDepth?: 2 | 3
  maxEntries?: number
}

interface BuildReportPdfModelInput {
  title: string
  coverImageUrl?: string | null
  category?: string | null
  generatedLabel?: string
  preparedBy?: string | null
  summary?: string
  keyFindings?: string[]
  reportMarkdown?: string
  excludeTocTitles?: string[]
  tocMaxDepth?: 2 | 3
  tocMaxEntries?: number
}

export function slugifyReportHeading(title: string): string {
  return title
    .toLowerCase()
    .replace(/&[^;]+;/g, '')
    .replace(/<[^>]+>/g, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
}

export function extractReportHeadingEntries(
  markdown: string,
  options: ExtractReportHeadingEntriesOptions = {},
): ReportPdfTocEntry[] {
  if (!markdown) return []
  const headings: ReportPdfTocEntry[] = []
  const excludedTitles = new Set((options.excludeTitles || []).map((title) => title.trim().toLowerCase()))
  const maxDepth = options.maxDepth ?? 3
  const maxEntries = options.maxEntries ?? Number.POSITIVE_INFINITY
  const seenIds = new Map<string, number>()
  const regex = /^#{1,3}\s+(.+?)\s*$/gm
  let match: RegExpExecArray | null

  while ((match = regex.exec(markdown)) !== null) {
    const hashes = match[0].match(/^#{1,3}/)?.[0] || '#'
    const title = match[1].trim()
    const level = hashes.length === 3 ? 3 : 2
    if (!title) continue
    if (excludedTitles.has(title.toLowerCase())) continue
    if (level > maxDepth) continue

    const baseId = slugifyReportHeading(title) || 'section'
    const seenCount = seenIds.get(baseId) || 0
    seenIds.set(baseId, seenCount + 1)

    headings.push({
      id: seenCount === 0 ? baseId : `${baseId}-${seenCount + 1}`,
      title,
      level,
    })

    if (headings.length >= maxEntries) {
      break
    }
  }

  return headings
}

// Extract H2/H3 headings from markdown text for Table of Contents
export function extractReportHeadings(markdown: string): string[] {
  return extractReportHeadingEntries(markdown, {
    excludeTitles: ['Executive Summary', 'Key Findings', 'References'],
  }).map((entry) => escapeHtml(entry.title))
}

export function buildReportPdfModel(input: BuildReportPdfModelInput): ReportPdfModel {
  const { process, citations } = createCitationProcessor()
  const reportMarkdown = input.reportMarkdown || ''
  const tocEntries = extractReportHeadingEntries(reportMarkdown, {
    excludeTitles: input.excludeTocTitles,
    maxDepth: input.tocMaxDepth,
    maxEntries: input.tocMaxEntries,
  })

  const cleanedSummary = input.summary ? reduceExcessiveBold(input.summary) : undefined
  const cleanedFindings = (input.keyFindings || []).map((f) => reduceExcessiveBold(f))
  const cleanedReport = reportMarkdown ? reduceExcessiveBold(reportMarkdown) : ''

  const processedSummary = cleanedSummary ? process(cleanedSummary) : undefined
  const processedFindings = cleanedFindings.map((finding) => process(finding))
  const processedReportMarkdown = cleanedReport ? process(cleanedReport) : ''
  const references = Object.entries(citations)
    .sort(([left], [right]) => Number(left) - Number(right))
    .map(([number, url]) => ({
      number: Number(number),
      url,
    }))

  return {
    title: normalizeTitleForCover(input.title) || 'Research Report',
    coverImageUrl: input.coverImageUrl,
    category: input.category,
    generatedLabel: input.generatedLabel,
    preparedBy: input.preparedBy,
    summary: processedSummary,
    keyFindings: processedFindings,
    reportMarkdown: processedReportMarkdown,
    tocEntries,
    references,
  }
}
